import { Order } from '../../services/exports/Interfaces';
import RatingStars from '../review/RatingStars';
import { useMemo, useState } from 'react';
import { SCREENS } from '../../services/exports/Constants';
import OrderRatesManager from '../../services/api/OrderRatesManager';
import useRouter from '../../hooks/navigation/useRouter';
import { useTranslation } from 'react-i18next';
import useGoogleReview from '../../hooks/google-review/useGoogleReview';
import useAuth from '../../hooks/global/useAuth';
import classnames from 'classnames';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import { Routes } from '../../modules/mobile/exports/Enums';
import { useLocation } from 'react-router-dom';

interface Props {
  order: Order;
  classes?: { container?: string; wrapper?: string };
  onSelected?: () => void;
}

export default function OrderRating({ order, classes, onSelected }: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Order:OrderRating' });

  const { company } = order;
  const { isLoggedIn } = useAuth();
  const { navigate } = useRouter();
  const location = useLocation();
  const { isMobileApp } = useMobileApp();
  const { loading, hasGooglePromotion, hasUploadedGoogleReview } = useGoogleReview({ autoLoad: true });

  const [rating, setRating] = useState(order.rates[0]?.rate);

  const showRating = useMemo(
    () => isLoggedIn && hasGooglePromotion && !hasUploadedGoogleReview,
    [isLoggedIn, hasGooglePromotion, hasUploadedGoogleReview],
  );

  function onSelect(rate: number): void {
    if (rate >= 4) {
      setTimeout(() => {
        if (isMobileApp) {
          onSelected?.();

          return navigate({
            mobile: {
              path: Routes.UploadGoogleReview,
              payload: {
                company,
              },
            },
          });
        }

        navigate({
          web: {
            to: SCREENS.UPLOAD_GOOGLE_REVIEW_SCREEN,
            options: {
              state: {
                prevPage: location.pathname,
                rating: rate,
                googleReviewHeaderTitle: t('upload_google_review.title'),
                company,
              },
            },
          },
          mobile: {
            path: Routes.UploadGoogleReview,
            payload: {
              company,
            },
          },
        });
      }, 500);
    }

    OrderRatesManager.create(order.id, { rate });
  }

  return (
    showRating &&
    !loading && (
      <div className={classes?.container}>
        <h6 className="text-sm">{t('title', { name: company?.name })}</h6>
        <p className="mt-xs text-muted-foreground text-xs">{t('description')}</p>
        <div
          className={classnames(
            'mt-sm w-fit flex justify-center bg-green dark:bg-[#191919] p-md rounded-[16px] shadow-section dark:shadow-none',
            classes?.wrapper,
          )}
        >
          <RatingStars rating={rating} setRating={setRating} onSelect={onSelect} />
        </div>
      </div>
    )
  );
}
